import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './components/header/header.component';
import { JoinApplyModalComponent } from './components/joinApplyModal/joinApplyModal.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { NotificationEventService } from './services/notificationEvent.service';
import { provideCommonAngularModules } from '@share/provideAngularModules';
import { AuthService } from './services/auth.service';
import { JoinApplyService } from './services/join-apply/join-apply.service';
import { WebSocketJs } from './services/websocket/websocket-js';
import { environment } from 'environments/environment';
import { LanguageService } from './services/language.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { AddCustomizeIconService } from './services/addCustomizeIcon.service';
import { RuntimeConfigService } from './services/runtime-config.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, JoinApplyModalComponent, ...provideCommonAngularModules()],
  templateUrl: './app.component.html',
  styleUrl: './app.component.less'
})
export class AppComponent implements OnInit, OnDestroy {
  notificationEventService: NotificationEventService = inject(NotificationEventService);
  authService = inject(AuthService);
  addCustomizeIconService = inject(AddCustomizeIconService);
  private webSocketJs = inject(WebSocketJs);
  private joinApplyService = inject(JoinApplyService);
  private runtimeConfigService = inject(RuntimeConfigService);
  isCollapsed = false;
  $unsubscribe = new Subject<void>();
  route = inject(ActivatedRoute);
  languageService = inject(LanguageService);
  isHiddenFlag: boolean = false;
  private wsInitialized = false;

  ngOnInit() {
    this.addCustomizeIconService.addIcon();
    this.notificationEventService.isCollapsedEvent.pipe(takeUntil(this.$unsubscribe)).subscribe((isCollapsed: boolean) => {
      this.isCollapsed = isCollapsed;
    });
    this.notificationEventService.isHiddenOverflow.pipe(takeUntil(this.$unsubscribe)).subscribe(isHiddenFlag => {
      const timer = setTimeout(() => {
        clearTimeout(timer)
        this.isHiddenFlag = isHiddenFlag;
      });
    })
    this.setLanguage();
    this.initWebSocket();
  }

  ngOnDestroy(): void {
    this.disconnectWebSocket();
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  private setLanguage() {
    this.authService.userEvent.pipe(takeUntil(this.$unsubscribe)).subscribe(user => {
      if (user) {
        const userId = user._id;
        const storedPreferredLang = localStorage.getItem(userId + '_lang');
        if (storedPreferredLang) {
          this.notificationEventService.languageEvent.next(storedPreferredLang)
          this.languageService.useLanguage(storedPreferredLang);
        }
      }
    })
  }

  private initWebSocket() {
    this.authService.userEvent.pipe(takeUntil(this.$unsubscribe)).subscribe((user) => {
      if (user) {
        setTimeout(() => {
          this.connectWebSocket();
        }, 0);
      } else {
        this.disconnectWebSocket();
      }
    });

    if (this.authService.isLoggedIn()) {
      this.connectWebSocket();
    }
  }

  private connectWebSocket() {
    if (!this.authService.isLoggedIn()) {
      return;
    }

    const token = this.getWsAuthToken();
    if (!token) {
      return;
    }

    const ws = this.webSocketJs.ws;
    if (ws?.readyState === WebSocket.OPEN) {
      this.wsInitialized = true;
      this.joinApplyService.ensureSubscribed(this.webSocketJs);
      return;
    }
    if (ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    if (ws) {
      this.webSocketJs.close();
      this.joinApplyService.resetSubscription();
      this.wsInitialized = false;
    }

    const wsUrl = this.getWsUrl(token);
    const onWsOpen = () => {
      this.wsInitialized = true;
      this.joinApplyService.ensureSubscribed(this.webSocketJs);
      this.webSocketJs.resubscribe();
    };

    this.webSocketJs.init(
      wsUrl,
      (data, notification) => this.joinApplyService.handleCreate(data, notification),
      (data, notification) => this.joinApplyService.handleUpdate(data, notification),
      undefined,
      undefined,
      onWsOpen,
    );
    this.joinApplyService.subscribeToNotifications(this.webSocketJs);
  }

  private disconnectWebSocket() {
    if (!this.webSocketJs.ws && !this.wsInitialized) {
      return;
    }
    this.webSocketJs.close();
    this.joinApplyService.resetSubscription();
    this.wsInitialized = false;
  }

  /** 与 dashboard 一致：优先使用 user.token（Tiledesk JWT） */
  private getWsAuthToken(): string {
    const user = this.authService.getUser();
    return (user?.token || this.authService.getAuthToken() || '').trim();
  }

  private getWsUrl(token: string): string {
    const configuredWsUrl = this.runtimeConfigService.getWsUrl();
    const base = configuredWsUrl
      || (environment.wsUrl ? environment.wsUrl : undefined);
    const encodedToken = encodeURIComponent(token);
    if (base) {
      const separator = base.includes('?') ? '&' : '?';
      return `${base}${separator}token=${encodedToken}`;
    }
    const loc = window.location;
    const protocol = loc.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${loc.host}/ws/?token=${encodedToken}`;
  }
}
